"use client";

import { useEffect, useMemo, useRef, useState } from "react";

import { Icon } from "@/components/ui/icon";
import { useListSnippets } from "@/lib/api/generated";
import { SNIPPET_TABS } from "@/features/snippets/components/list/toolbar";
import type { SnippetTab } from "@/features/snippets/lib/types";
import { cn } from "@/lib/utils";

import {
  PanelEmptyState,
  PanelFooter,
  PanelListSkeleton,
  PanelNoResults,
  PanelSearch,
  PanelShell,
  PanelTabs,
  useDebouncedValue,
} from "./panel-ui";
import type { SnippetInsertPayload } from "./types";

interface SnippetsPanelProps {
  onInsert: (payload: SnippetInsertPayload) => void;
  onClose: () => void;
  /** Open the floating snippet editor in create mode. */
  onCreate?: () => void;
  /** Open the floating snippet editor in edit mode for the given snippet. */
  onEdit?: (snippetId: string) => void;
  /** Navigate to the snippets management page. */
  onConfigure?: () => void;
}

/**
 * Standalone snippets popover: All/My tabs, a variables-style search bar
 * scoped to the active tab, skeleton loading, a rich empty state, and a
 * footer with Create / Configure actions.
 *
 * Snippets are inserted as raw content (not entities), so clicking a row drops
 * its body into the editor; the pencil opens the floating editor to manage the
 * snippet in the library without ever referencing it in the sequence.
 */
export function SnippetsPanel({
  onInsert,
  onClose,
  onCreate,
  onEdit,
  onConfigure,
}: SnippetsPanelProps) {
  const [tab, setTab] = useState<SnippetTab>("all");
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const { data, isLoading } = useListSnippets({
    selector: tab === "my" ? "my" : undefined,
    search: debouncedSearch || undefined,
  });

  const snippets = useMemo(() => data?.snippets ?? [], [data?.snippets]);
  const hasSearch = debouncedSearch.trim().length > 0;

  return (
    <PanelShell>
      <PanelTabs tabs={SNIPPET_TABS} value={tab} onChange={setTab} />
      <PanelSearch
        ref={inputRef}
        placeholder="Search snippets…"
        value={search}
        onChange={setSearch}
        onEscape={onClose}
      />

      {isLoading ? (
        <PanelListSkeleton lines={2} showLeadingIcon />
      ) : snippets.length === 0 ? (
        hasSearch ? (
          <PanelNoResults label="No snippets match your search." />
        ) : (
          <PanelEmptyState
            icon="IconScissors2"
            title={tab === "my" ? "No snippets of your own yet" : "No snippets yet"}
            description="Reusable blocks of text you can drop into any message."
            linkLabel="Learn more about snippets"
            onLink={onConfigure}
          />
        )
      ) : (
        <ul className="flex max-h-[280px] flex-col gap-0.5 overflow-y-auto p-1.5">
          {snippets.map((snippet) => (
            <li key={snippet.id}>
              <div className="group flex w-full items-center gap-1 rounded-md transition-colors hover:bg-contrast/6">
                <button
                  type="button"
                  onClick={() =>
                    onInsert({
                      bodyDoc: snippet.body_doc as Record<string, unknown>,
                    })
                  }
                  className="flex min-w-0 flex-1 items-center gap-2 rounded-md px-2 py-1.5 text-left"
                >
                  <Icon
                    name="IconScissors2"
                    size={32}
                    className="shrink-0 opacity-60"
                  />
                  <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="w-full truncate text-sm font-medium text-heading">
                      {snippet.name}
                    </span>
                    <span className="w-full truncate text-[11px] text-contrast/50">
                      {snippet.visibility === "private" ? "Private" : "Shared"}
                      {snippet.creator_name ? ` · ${snippet.creator_name}` : ""}
                    </span>
                  </span>
                </button>
                {onEdit && snippet.can_edit ? (
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      onEdit(snippet.id);
                    }}
                    className={cn(
                      "mr-1 flex size-6 shrink-0 cursor-pointer items-center justify-center rounded",
                      "opacity-0 transition-opacity group-hover:opacity-60 hover:!opacity-100",
                    )}
                    aria-label={`Edit ${snippet.name}`}
                  >
                    <Icon name="IconPencil" size={14} className="text-contrast" />
                  </button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}

      <PanelFooter onCreate={onCreate} onConfigure={onConfigure} />
    </PanelShell>
  );
}
